import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import TabNavigation from '../../components/ui/TabNavigation';
import CustomerTable from './components/CustomerTable';
import CustomerCard from './components/CustomerCard';
import CustomerFormModal from './components/CustomerFormModal';
import CustomerHistoryModal from './components/CustomerHistoryModal';
import SearchAndFilter from './components/SearchAndFilter';
import BookingModal from '../../components/ui/BookingModal';
import Icon from '../../components/AppIcon';
import { formatDateForDB } from '../../utils/dateUtils';

const CustomerDatabase = () => {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [bookingCustomer, setBookingCustomer] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch customers from Supabase with retry logic for schema cache
  const fetchCustomers = async (retryCount = 0) => {
    try {
      const { data, error } = await supabase?.from('customers')?.select('*')?.order('created_at', { ascending: false });

      if (error) {
        // Handle schema cache error with retry
        if (error?.code === 'PGRST205' && retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.log(`Schema cache not ready, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchCustomers(retryCount + 1);
        }
        throw error;
      }
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      if (err?.code === 'PGRST205') {
        setError('Database schema is updating. Please refresh the page in a moment.');
      } else {
        setError('Failed to load customers');
      }
    }
  };

  // Fetch appointments from Supabase
  const fetchAppointments = async () => {
    try {
      const { data, error } = await supabase?.from('bookings')?.select('*')?.order('date', { ascending: false });

      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchAppointments()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = customers?.filter(customer => {
      const matchesSearch = customer?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           customer?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           customer?.phone?.includes(searchTerm);
      const matchesService = filterService === 'all' || customer?.service_preference === filterService;
      return matchesSearch && matchesService;
    });

    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'recent':
          return new Date(b?.last_appointment || 0) - new Date(a?.last_appointment || 0);
        case 'visits':
          return (b?.total_visits || 0) - (a?.total_visits || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [customers, searchTerm, filterService, sortBy]);

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setIsFormModalOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setIsFormModalOpen(true);
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        name: customerData?.name,
        email: customerData?.email,
        phone: customerData?.phone,
        service_preference: customerData?.servicePreference || '',
        notes: customerData?.notes || ''
      };

      if (editingCustomer) {
        // Update existing customer
        const { data, error } = await supabase?.from('customers')?.update(dbData)?.eq('id', editingCustomer?.id)?.select()?.single();

        if (error) throw error;
        setCustomers(prev => prev?.map(c => c?.id === data?.id ? data : c));
      } else {
        // Create new customer
        const { data, error } = await supabase?.from('customers')?.insert([dbData])?.select()?.single();

        if (error) throw error;
        setCustomers(prev => [data, ...prev]);
      }

      setIsFormModalOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      console.error('Error saving customer:', err);
      throw new Error('Failed to save customer. Please try again.');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer? This will also delete all their appointments.')) {
      try {
        const { error } = await supabase?.from('customers')?.delete()?.eq('id', customerId);

        if (error) throw error;

        setCustomers(prev => prev?.filter(c => c?.id !== customerId));
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert('Failed to delete customer. Please try again.');
      }
    }
  };

  const handleScheduleBooking = (customer) => {
    setBookingCustomer(customer);
    setIsBookingModalOpen(true);
  };

  const handleSaveBooking = (bookingData) => {
    navigate('/booking-management', {
      state: {
        newBooking: bookingData,
        customerId: bookingCustomer?.id
      }
    });
  };

  const handleViewHistory = (customer) => {
    setHistoryCustomer(customer);
    setIsHistoryModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading customer data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error text-lg mb-2">Error Loading Data</p>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => window.location?.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stats = {
    total: customers?.length || 0,
    active: customers?.filter(c => c?.last_appointment)?.length || 0,
    new: customers?.filter(c => !c?.last_appointment)?.length || 0
  };

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <div className="container mx-auto px-4 py-6 md:py-8 lg:py-10">
        <div className="flex flex-col gap-6 md:gap-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Customer Database
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage your customer information and appointment history
              </p>
            </div>
            <button
              onClick={handleAddCustomer}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth w-full md:w-auto"
            >
              <Icon name="Plus" size={20} />
              <span>Add Customer</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Customers</span>
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.total}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Active Customers</span>
                <Icon name="UserCheck" size={20} className="text-success" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.active}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">New Customers</span>
                <Icon name="UserPlus" size={20} className="text-primary" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.new}</p>
            </div>
          </div>

          <SearchAndFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterService={filterService}
            onFilterChange={setFilterService}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            searchQuery={searchTerm}
            filterStatus={filterService}
            onFilterStatusChange={setFilterService}
            onAddNew={handleAddCustomer}
            onExport={() => {}}
            onBulkDelete={() => {}}
          />

          {viewMode === 'table' ? (
            <CustomerTable
              customers={filteredAndSortedCustomers}
              onEdit={handleEditCustomer}
              onDelete={handleDeleteCustomer}
              onSchedule={handleScheduleBooking}
              onViewHistory={handleViewHistory}
              onScheduleBooking={handleScheduleBooking}
              onSelectCustomer={() => {}}
              onSelectAll={() => {}}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredAndSortedCustomers?.map((customer) => (
                <CustomerCard
                  key={customer?.id}
                  customer={customer}
                  onEdit={handleEditCustomer}
                  onDelete={handleDeleteCustomer}
                  onSchedule={handleScheduleBooking}
                  onViewHistory={handleViewHistory}
                  onScheduleBooking={handleScheduleBooking}
                  isSelected={false}
                  onSelect={() => {}}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CustomerFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingCustomer(null);
        }}
        onSave={handleSaveCustomer}
        customer={editingCustomer}
      />
      <CustomerHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => {
          setIsHistoryModalOpen(false);
          setHistoryCustomer(null);
        }}
        customer={historyCustomer}
        appointments={appointments?.filter(apt => apt?.customer_id === historyCustomer?.id)}
      />
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setBookingCustomer(null);
        }}
        onSave={handleSaveBooking}
        onDelete={() => {}}
        customers={customers}
        selectedDate={formatDateForDB(new Date())}
      />
    </div>
  );
};

export default CustomerDatabase;